import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { PROFILE_PUBLIC_COLUMNS } from "@/lib/profileColumns";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import ProfileQuickView from "@/components/ProfileQuickView";
import type { Database } from "@/integrations/supabase/types";
import Seo from "@/components/Seo";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

const PublicProfile = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { navigate("/"); return; }
    if (authLoading) return;

    const fetch = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("profiles")
        .select(PROFILE_PUBLIC_COLUMNS as any)
        .eq("user_id", userId)
        .maybeSingle<any>();

      if (!data) {
        setProfile(null);
        setLoading(false);
        return;
      }

      const isOwnProfile = user?.id === userId;

      // Les restrictions commerciales / de vérification n'empêchent plus
      // l'affichage d'un compte PRO. Seule une suspension admin active le masque.
      if (role !== "admin" && !isOwnProfile) {
        const activelySuspended =
          Boolean(data.suspended_at) &&
          (!data.suspended_until || new Date(data.suspended_until).getTime() > Date.now());
        if (activelySuspended) {
          setProfile(null);
          setLoading(false);
          return;
        }
      }

      setProfile(data);
      setLoading(false);
    };
    fetch();
  }, [userId, navigate, user, role, authLoading]);


  const openParentConversation = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (role !== "parent" || !profile) return;
    const { data: existing, error: lookupError } = await supabase
      .from("conversations")
      .select("id")
      .eq("parent_id", user.id)
      .eq("pro_id", profile.user_id)
      .maybeSingle();
    if (lookupError) {
      console.error("Conversation lookup failed:", lookupError);
      return;
    }
    if (existing?.id) {
      navigate(`/messages?convo=${existing.id}`);
      return;
    }
    const { data: created, error: createError } = await supabase
      .from("conversations")
      .insert({ parent_id: user.id, pro_id: profile.user_id })
      .select("id")
      .single();
    if (createError) {
      console.error("Conversation creation failed:", createError);
      return;
    }
    navigate(`/messages?convo=${created.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-background">
        <Navbar />
        <div className="text-center py-16 text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen w-full bg-background">
        <Navbar />
        <div className="text-center py-16 text-muted-foreground">Profil introuvable.</div>
      </div>
    );
  }

  const proName = profile.full_name || "Professionnel de la garde d'enfants";
  const proCity = profile.city ? ` à ${profile.city}` : "";
  const proCategory = profile.pro_category || "Garde d'enfants";
  const cityForTitle = profile.city || "près de chez vous";
  const seoTitle = `${proName} — ${proCategory} ${profile.city ? `à ${profile.city}` : "sur Click&Gard"}`;
  const seoDesc = (profile.bio?.slice(0, 155) ||
    `Découvrez ${proName}, ${proCategory.toLowerCase()} ${cityForTitle} sur Click&Gard. Profil, tarifs, disponibilités et messagerie.`);
  const profileUrl = `https://clickandgard.fr/profil/${profile.user_id}`;
  const hourlyRate = (profile as { hourly_rate?: number | null }).hourly_rate;

  return (
    <div className="min-h-screen w-full bg-background">
      <Seo
        title={seoTitle}
        description={seoDesc}
        type="profile"
        path={`/profil/${profile.user_id}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: `${proName} — ${proCategory}`,
            description: seoDesc,
            url: profileUrl,
            image: profile.avatar_url || undefined,
            serviceType: proCategory,
            areaServed: profile.city
              ? { "@type": "City", name: profile.city, address: { "@type": "PostalAddress", addressCountry: "FR" } }
              : { "@type": "Country", name: "France" },
            address: profile.city
              ? { "@type": "PostalAddress", addressLocality: profile.city, addressCountry: "FR" }
              : undefined,
            priceRange: hourlyRate ? `${hourlyRate} EUR / heure` : "€€",
            provider: {
              "@type": "Person",
              name: proName,
              jobTitle: proCategory,
              image: profile.avatar_url || undefined,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://clickandgard.fr/" },
              { "@type": "ListItem", position: 2, name: "Rechercher", item: "https://clickandgard.fr/search" },
              { "@type": "ListItem", position: 3, name: proName, item: profileUrl },
            ],
          },
        ]}
      />
      <Navbar />
      <ProfileQuickView
        profile={profile}
        open={true}
        modal={false}
        onOpenChange={(open) => { if (!open) navigate(-1); }}
        onMessage={role === "parent" ? openParentConversation : undefined}
      />
    </div>
  );
};

export default PublicProfile;
